import React, { ReactElement } from "react";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import { DateDisplay } from "../DateDisplay";
import { FileSetDetailResponse, InvoiceFileResponse } from "../../models/Invoice";
import { Typography } from "@material-ui/core";
import { InvoiceFileSetNoteSection } from "./InvoiceFileSetNoteSection";
import { InvoiceFileSetHistorySection } from "./InvoiceFileSetHistorySection";
import { InvoiceFileSetParsingErrorSection } from "./InvoiceFileSetParsingErrorSection";
import { InvoiceFileSetFileSection } from "./InvoiceFileSetFileSection";
import { AxiosResponse } from "axios";
import colors from "../../constants/colors";

interface InvoiceFileSetDetailDisplayCardProps {
	//The inoice set to be displayed in the card.
	invoiceSet: FileSetDetailResponse;
	//The callback method to be passed to the various sections of the card to set the dialog contents for the page.
	setDialogContents: (title: string, element: ReactElement | undefined, titleBarContent?: ReactElement) => void;
	//The method for calling the api for updating the note for the invoice.
	updateNote: (invoiceHistoryId: string, newNote: string) => void;
	//The callback method for handling reprocessing of the invoice.
	handleReprocessing: (invoiceHistoryId: string) => void;
	//The callback method for handling the submission of the file set to accounting.
	handleFileSubmission: (invoiceHistoryId: string, accountingFileName: string) => void;
	//The callback method for retrieving the invoice or accounting file to be displayed in the dialog.
	retrieveFile: (invoiceHistoryId: string, fileName: string) => Promise<AxiosResponse<InvoiceFileResponse>>;
	//The callback method for generating a download of one of the files.
	generateFileDownloadLink: (invoiceHistoryId: string, fileName: string) => string;
}

const useStyles = makeStyles((theme) => ({
	card: {
		borderRadius: theme.spacing(0.5),
		width: theme.breakpoints.values.md - 30,
	},
	title: {
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: ".1em",
	},
	cardContent: {
		padding: theme.spacing(1),
	},
}));

/**
 * This display card takes in an invoice set and displays various information about the invoice set
 *  with the help of components for each section of the card.
 * @param props
 */
export const InvoiceFileSetDisplayCard = (props: InvoiceFileSetDetailDisplayCardProps) => {
	const classes = useStyles();
	const setNote = (newNote: string) => props.updateNote(props.invoiceSet.invoiceHistoryId, newNote);

	return (
		<Card className={classes.card} data-testid="user-display-card">
			<div className={classes.cardContent}>
				<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
					<span
						style={{
							textOverflow: "ellipsis",
							overflow: "hidden",
							marginLeft: 10,
						}}
						data-testid="card-title"
					>
						<Typography title={props.invoiceSet.invoiceFileName}>{props.invoiceSet.invoiceFileName}</Typography>
					</span>
					<div
						style={{
							fontSize: 12,
							paddingTop: 4,
							justifyContent: "flex-end",
							paddingBottom: 10,
						}}
					>
						Upload Date: <DateDisplay date={props.invoiceSet.invoiceFileUploadDate} fontSize={12} />
					</div>
				</div>
				<div className={classes.cardContent}>
					{props.invoiceSet.accountingFileSendDate !== null && (
						<Typography style={{ color: colors.green600 }}>
							Sent to Accounting on: <DateDisplay date={props.invoiceSet.accountingFileSendDate!} fontSize={16} />
						</Typography>
					)}
				</div>
				<div>
					<InvoiceFileSetParsingErrorSection
						errorList={props.invoiceSet.errors}
						errorCount={props.invoiceSet.errorCount}
						setDialogContents={props.setDialogContents}
					></InvoiceFileSetParsingErrorSection>
				</div>
				<div>
					<InvoiceFileSetHistorySection
						historyList={props.invoiceSet.history}
						setDialogContents={props.setDialogContents}
					></InvoiceFileSetHistorySection>
				</div>
				<div>
					<InvoiceFileSetNoteSection
						setDialogContents={props.setDialogContents}
						setNote={setNote}
						note={props.invoiceSet.note}
					></InvoiceFileSetNoteSection>
				</div>
				<div>
					<InvoiceFileSetFileSection
						retrieveFile={props.retrieveFile}
						handleReprocessing={props.handleReprocessing}
						invoiceFileName={props.invoiceSet.invoiceFileName}
						accountingFileName={props.invoiceSet.accountingFileName}
						generateFileDownloadLink={props.generateFileDownloadLink}
						handleFileSubmission={props.handleFileSubmission}
						setDialogContent={props.setDialogContents}
						invoiceHistoryId={props.invoiceSet.invoiceHistoryId}
					></InvoiceFileSetFileSection>
				</div>
			</div>
		</Card>
	);
};
